import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Link,
  useColorMode,
} from "@chakra-ui/react";
import Typography, { TVariant } from "../../DSL/Typography/Typography";
import { lightOrDarkMode } from "../../DSL/Theme";

const faqs = [
  {
    question: "What are Doge Pixels?",
    answer: (
      <>
        A Pixel is a visual representation of your ownership in the Doge image. They are NFTs which can be minted by
        locking 55,240 DOG on the Pixel Portal. They act as a first class ticket to unlimited Doge entertainment. Pixel
        holders are prioritized in all of our projects and receive Pixel Perks like airdrops and other rewards.
      </>
    ),
  },
  {
    question: "How do I mint a new Pixel?",
    answer: (
      <>
        Check out our{" "}
        <Link
          href="https://www.youtube.com/watch?v=example"
          isExternal
          textDecoration="underline"
        >
          video tutorial from V2
        </Link>{" "}
        which will be very similar to how it is set up in V3, or follow the steps below:
        <Box as="ol" pl={5} mt={2} listStyleType="decimal">
          <Box as="li" mb={1}>
            Go to{" "}
            <Link href="https://pixelportal.app" isExternal textDecoration="underline">
              pixelportal.app
            </Link>
          </Box>
          <Box as="li" mb={1}>Connect your wallet</Box>
          <Box as="li" mb={1}>Click on Mint</Box>
          <Box as="li" mb={1}>
            Select the number of Pixels you wish to mint. Make sure you have enough DOG tokens in your wallet.
            You can get DOG on Base here:{" "}
            <Link href="https://aerodrome.finance" isExternal textDecoration="underline">
              Aerodrome
            </Link>
            {" "}or{" "}
            <Link href="https://defillama.com" isExternal textDecoration="underline">
              Defillama
            </Link>
          </Box>
          <Box as="li" mb={1}>Click Mint and approve the transaction in your wallet.</Box>
        </Box>
        <Box mt={3}>
          <strong>Tip:</strong> Clear your browser history/cache before transacting.
        </Box>
        <Box mt={2}>
          <Link
            href="https://docs.google.com/document/d/1II6c9CSHeEsWdB6kQctjCotNlKzoOAZchCQelkcUyPk/edit?usp=sharing"
            isExternal
            textDecoration="underline"
          >
            Dev notes on mint, claim and burn process
          </Link>
        </Box>
      </>
    ),
  },
  {
    question: "If I own a Pixel already, am I able to keep my exact pixel or will I lose it?",
    answer: (
      <>
        If you hold a Pixel from V1 on ETH or V2 on Base, follow the steps below to claim on V3:
        <Box as="ol" pl={5} mt={2} listStyleType="decimal">
          <Box as="li" mb={1}>
            Go to{" "}
            <Link href="https://pixelportal.app" isExternal textDecoration="underline">
              pixelportal.app
            </Link>
          </Box>
          <Box as="li" mb={1}>Connect your wallet</Box>
          <Box as="li" mb={1}>Click on Claim button</Box>
          <Box as="li" mb={1}>Follow onscreen prompts to sign your transaction in your connected wallet</Box>
          <Box as="li" mb={1}>
            The system will check your wallet for eligibility and auto-pull any V1 and V2 pixels you hold
          </Box>
          <Box as="li" mb={1}>Confirm you want to claim (this action cannot be reversed)</Box>
          <Box as="li" mb={1}>
            If done correctly, you should receive a message confirming all pixels have been claimed on V3
          </Box>
        </Box>
        <Box mt={3}>
          * If you'd like to keep your pixel on V1, you may do so, but keep in mind that all old pixel portals will be
          sunset. We recommend claiming your legacy pixels on V3.
        </Box>
      </>
    ),
  },
  {
    question: "What if my V1 and V2 pixels are in separate wallets — can I still claim all?",
    answer: (
      <>
        Yes, you will need to go through the claims process for each individual wallet containing Pixels.
      </>
    ),
  },
  {
    question: "Was a snapshot taken for the claims process?",
    answer: (
      <>
        Yes, a snapshot was taken on March 12th. The wallet in which you held V1 or V2 Pixels at that time is the one
        you will need to use to claim them on V3.
      </>
    ),
  },
  {
    question: "How do I burn my pixels?",
    answer: (
      <>
        You must first claim your pixels on Pixel Portal V3 in order to burn them. Once you have connected a wallet and
        the system verifies your pixels, follow the steps below:
        <Box as="ol" pl={5} mt={2} listStyleType="decimal">
          <Box as="li" mb={1}>
            Go to{" "}
            <Link href="https://pixelportal.app" isExternal textDecoration="underline">
              pixelportal.app
            </Link>
          </Box>
          <Box as="li" mb={1}>Connect your wallet</Box>
          <Box as="li" mb={1}>Click on Burn button</Box>
          <Box as="li" mb={1}>
            A screen will pop up showing your V3 pixels — select which ones you want to burn
          </Box>
          <Box as="li" mb={1}>
            The total amount of $DOG you will receive is shown for your selected pixel(s)
          </Box>
          <Box as="li" mb={1}>Select "Burn" and sign the transaction in your wallet</Box>
          <Box as="li" mb={1}>A confirmation message will appear once complete</Box>
        </Box>
      </>
    ),
  },
  {
    question: "Will V3 pixels on Base work the same as Legacy Pixels on ETH?",
    answer: (
      <>
        Yes, V3 Pixels will be applicable for Pixel Perks, are "liquid," and redeemable at any time for $DOG token.
        <Box mt={2}>
          Please note that Pixel Portal V3 on Base will be the only active portal going forward for Doge Pixels.
        </Box>
      </>
    ),
  },
  {
    question: "Do Pixels have traits?",
    answer: (
      <>
        Yes — an exciting update with our new V3 Pixel Portal is that we have added traits to all pixels. If you mint a
        pixel and feel that your trait is incorrect, we will have a system to resolve pixel coordinate discrepancies.
      </>
    ),
  },
  {
    question: 'How do I get a "rare" Pixel?',
    answer: (
      <>
        All pixels are received at random. Every pixel has an equal chance of generation, and any pixel considered rare
        is a matter of opinion. You can always burn and remint, or purchase a pixel on the secondary market.
      </>
    ),
  },
  {
    question: "What does it mean when we say Pixels are liquid NFTs?",
    answer: (
      <>
        Your pixels will always be "liquid" — you can redeem them for 55,240 DOG at any time through the smart contract.
      </>
    ),
  },
  {
    question: "What's the significance of holding a Doge Pixel if I already own the Doge NFT through $DOG?",
    answer: (
      <>
        Pixels allow you to have visual ownership of your piece of the iconic Doge meme and unlock exclusive Pixel Perks
        which include NFT drops, rewards, and experiences.
      </>
    ),
  },
  {
    question: "Will there be any additional fees to burn pixel(s)?",
    answer: <>No, there will not be any fees should you decide to burn your pixel.</>,
  },
  {
    question: "Is there a leaderboard?",
    answer: (
      <>
        Yes — click the "Leaderbork" button in the menu bar at the top. It will give you a list of Base names and wallet
        addresses ranked by total pixels held.
        <Box mt={2}>
          <strong>Important:</strong> You will need to claim your V1 and V2 pixels first in order to see them on
          Leaderbork. All newly minted V3 Doge pixels will auto-populate to Leaderbork.
        </Box>
      </>
    ),
  },
];

const HelpPage = () => {
  const { colorMode } = useColorMode();

  const borderColor = lightOrDarkMode(colorMode, "yellow.600", "purple.300");
  const panelBg = lightOrDarkMode(colorMode, "yellow.100", "purple.800");
  const hoverBg = lightOrDarkMode(colorMode, "yellow.200", "purple.700");

  return (
    <Box w="full" mt={6} p={{ base: 6, md: 0 }}>
      <Typography mb={6} block textAlign="center" variant={TVariant.PresStart24}>
        FAQ
      </Typography>
      <Flex justifyContent="center">
        <Box maxW="3xl" w="full">
          <Accordion allowToggle>
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="8px"
                mb={3}
                overflow="hidden"
              >
                {({ isExpanded }) => (
                  <>
                    <AccordionButton
                      py={4}
                      px={5}
                      _hover={{ bg: hoverBg }}
                      bg={isExpanded ? panelBg : "transparent"}
                    >
                      <Box flex="1" textAlign="left" pr={3}>
                        <Typography variant={TVariant.PresStart12}>{faq.question}</Typography>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={5} pt={3} px={5} bg={panelBg}>
                      <Typography variant={TVariant.ComicSans16} block>
                        {faq.answer}
                      </Typography>
                    </AccordionPanel>
                  </>
                )}
              </AccordionItem>
            ))}
          </Accordion>
        </Box>
      </Flex>
    </Box>
  );
};

export default HelpPage;
